import React, { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowLeft } from 'lucide-react';
import NewsletterSignup from './NewsletterSignup';
import BrandLogo from './BrandLogo';
import LesterChat from './LesterChat';

const NAV = [
  { to: '/explore', label: 'Explore', testid: 'nav-explore' },
  { to: '/towns', label: 'Towns', testid: 'nav-towns' },
  { to: '/events', label: 'Events', testid: 'nav-events' },
  { to: '/explore?category=food_drink', label: 'Eat & Drink', testid: 'nav-eat-drink' },
  { to: '/accommodation', label: 'Stay over', testid: 'nav-accommodation' },
  { to: '/awards', label: 'Awards', testid: 'nav-awards' },
  { to: '/packages', label: 'Stay & Save', testid: 'nav-packages' },
  { to: '/plan', label: 'Plan My Day', testid: 'nav-plan' },
  { to: '/half-days', label: 'Half-days', testid: 'nav-half-days' },
  { to: '/itinerary', label: 'Build Itinerary', testid: 'nav-itinerary' },
  { to: '/partners', label: 'Trade Partners', testid: 'nav-partners', emphasis: true },
];

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // For links that share the same pathname (Explore vs Eat & Drink both → /explore),
  // disambiguate by matching the `category` query param exactly. Otherwise both
  // links would highlight simultaneously which looks broken.
  const isNavActive = (to, isActive) => {
    const [path, query] = to.split('?');
    if (path !== location.pathname) return false;
    const wantedCategory = new URLSearchParams(query || '').get('category') || '';
    const currentCategory = new URLSearchParams(location.search).get('category') || '';
    return wantedCategory === currentCategory && isActive;
  };
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-base/80 border-b border-charcoal/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center"
          data-testid="header-logo"
          onClick={() => {
            // If already on the homepage, the route doesn't change so the
            // global ScrollToTop won't fire — scroll explicitly.
            if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <BrandLogo size="md" />
        </Link>
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) => {
                const active = isNavActive(n.to, isActive);
                if (n.emphasis) {
                  return `px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                    active
                      ? 'bg-heritage text-base'
                      : 'text-heritage hover:bg-heritage/10'
                  }`;
                }
                return `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? 'bg-charcoal text-base'
                    : 'text-charcoal/80 hover:text-charcoal hover:bg-stone/60'
                }`;
              }}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <Link
          to="/packages"
          data-testid="header-cta-book"
          className="hidden lg:inline-flex items-center gap-2 bg-heritage text-base px-6 py-3 rounded-full font-semibold hover:bg-heritage-deep transition-all hover:scale-[1.03] active:scale-95"
        >
          Book a Break
        </Link>
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2"
          data-testid="header-mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-nav-panel"
        >
          {open ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
        </button>
      </div>
      {open && (
        <div id="mobile-nav-panel" className="lg:hidden border-t border-charcoal/10 bg-base">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) => {
                  const active = isNavActive(n.to, isActive);
                  if (n.emphasis) {
                    return `px-4 py-3 rounded-2xl text-base font-bold ${
                      active ? 'bg-heritage text-base' : 'text-heritage'
                    }`;
                  }
                  return `px-4 py-3 rounded-2xl text-base font-medium ${
                    active ? 'bg-charcoal text-base' : 'text-charcoal'
                  }`;
                }}
                data-testid={`mobile-${n.testid}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/packages" onClick={() => setOpen(false)} className="mt-3 bg-heritage text-base px-5 py-3 rounded-full font-semibold text-center">
              Book a Break
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  // Same day-of-week logic as HomePage's hero — keep the copy contextual.
  // Fri/Sat/Sun emphasise the weekend; Mon–Thu lean into midweek escapes.
  const dow = new Date().getDay(); // 0=Sun … 6=Sat
  const weekend = dow === 0 || dow === 5 || dow === 6;
  const eyebrow = weekend ? 'Free · Every Friday · 1-click unsubscribe' : 'Free · Every week · 1-click unsubscribe';
  const headline = weekend ? 'Get the weekend, served warm.' : 'Make midweek matter.';
  const copy = weekend
    ? "One Friday email: 1 plan for the weekend, 3 things to do midweek, plus the weather. ~30 seconds to read."
    : 'One email a week: midweek escapes, evening events and what to do this weekend. ~30 seconds to read.';
  return (
    <footer className="bg-charcoal text-base/90 mt-24">
      {/* Newsletter promo strip — global visibility on every page */}
      <div className="border-b border-base/10" data-testid="footer-newsletter">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="label text-mustard mb-3" data-testid="footer-newsletter-eyebrow">{eyebrow}</div>
            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="footer-newsletter-headline">
              {headline}
            </h3>
            <p className="text-base/70 text-base md:text-lg mt-3 max-w-xl">
              {copy}
            </p>
            <p className="text-mustard/90 text-sm mt-3 max-w-xl">
              Bonus: subscribers go into our <Link to="/competition" className="underline hover:text-mustard">quarterly free prize draw</Link> for a StayPlayExplore voucher. UK, 18+.
            </p>
          </div>
          <div className="lg:col-span-5">
            <NewsletterSignup variant="dark" source="footer" />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="col-span-2 lg:col-span-4">
          <Link to="/" className="inline-flex" data-testid="footer-logo">
            <BrandLogo size="lg" dark />
          </Link>
          <p className="mt-6 text-base/70 text-lg leading-relaxed max-w-md">
            Holidays, days out and weekends in the heart of the country. Born in Leicestershire — for everyone who loves it here.
          </p>
        </div>
        <div className="lg:col-span-2 lg:col-start-6">
          <div className="label text-base/50 mb-4">Explore</div>
          <ul className="space-y-3">
            <li><Link to="/explore" className="hover:text-mustard">Attractions</Link></li>
            <li><Link to="/towns/leicester" className="hover:text-mustard" data-testid="footer-visit-leicester">Visit Leicester</Link></li>
            <li><Link to="/towns" className="hover:text-mustard">Explore Leicestershire</Link></li>
            <li><Link to="/events" className="hover:text-mustard">What&apos;s On — events</Link></li>
            <li><Link to="/packages" className="hover:text-mustard">Short breaks</Link></li>
          </ul>
        </div>
        <div className="lg:col-span-2">
          <div className="label text-base/50 mb-4">For visitors</div>
          <ul className="space-y-3">
            <li><Link to="/plan" className="hover:text-mustard">Plan My Day</Link></li>
            <li><Link to="/half-days" className="hover:text-mustard">Half-day loops</Link></li>
            <li><Link to="/digest" className="hover:text-mustard">This Weekend</Link></li>
            <li><Link to="/awards" className="hover:text-mustard">Tourism Awards voting</Link></li>
            <li><a href="https://www.stayplayexplore.co.uk/" target="_blank" rel="noreferrer" className="hover:text-mustard">StayPlayExplore</a></li>
          </ul>
        </div>
        <div className="lg:col-span-2">
          <div className="label text-base/50 mb-4">For business</div>
          <ul className="space-y-3">
            <li><Link to="/services" className="hover:text-mustard font-semibold" data-testid="footer-services">For the tourism industry</Link></li>
            <li><Link to="/partners" className="hover:text-mustard" data-testid="footer-trade-partners">Trade Partners</Link></li>
            <li><Link to="/partners" className="hover:text-mustard text-sm text-base/65" data-testid="footer-apply-access">Apply for access →</Link></li>
          </ul>
        </div>
        <div className="lg:col-span-2">
          <div className="label text-base/50 mb-4">Get in touch</div>
          <p className="mt-1"><a href="mailto:info@goleicestershire.com" className="hover:text-mustard break-all">info@goleicestershire.com</a></p>
        </div>
      </div>

      <div className="border-t border-base/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-wrap items-center justify-between gap-3 text-sm text-base/50">
          <div>© {new Date().getFullYear()} Leicestershire Promotions Ltd. All rights reserved. Listings and events data © GoLeicestershire — republication without permission is prohibited.</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/about" data-testid="footer-about" className="hover:text-mustard">About us</Link>
            <Link to="/accessibility" data-testid="footer-accessibility" className="hover:text-mustard">Accessibility</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-mustard">Privacy</Link>
            <Link to="/cookies" data-testid="footer-cookies" className="hover:text-mustard">Cookies</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-mustard">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function BackBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Hide on home and admin pages. Admin has its own chrome.
  if (pathname === '/' || pathname.startsWith('/admin')) return null;
  const goBack = () => {
    // If there's no in-app history (e.g. user landed here via a direct link),
    // fall back to the homepage so the button is never a no-op.
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-4">
      <button
        type="button"
        onClick={goBack}
        data-testid="global-back-button"
        className="inline-flex items-center gap-1.5 text-sm text-charcoal/70 hover:text-heritage transition-colors"
        aria-label="Go back to the previous page"
      >
        <ArrowLeft size={16} aria-hidden="true" /> Back
      </button>
    </div>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen bg-base text-charcoal">
      <a href="#main-content" className="skip-to-content" data-testid="skip-to-content">
        Skip to main content
      </a>
      <Header />
      <BackBar />
      <main id="main-content" tabIndex={-1}><Outlet /></main>
      <Footer />
      <LesterChat />
    </div>
  );
}
